import { useState, useEffect, useCallback } from 'react';
import { getValidToken } from '../utils/auth';
import TradingDatePicker from './TradingDatePicker';
import { toDateStr } from '../utils/tradingCalendar';

const API_URL = process.env.REACT_APP_API_URL || '';

const SENTIMENT_COLOR = { bullish: '#22c55e', bearish: '#ef4444', neutral: '#9ca3af' };

function formatTime(unixMs) {
  return new Date(unixMs).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York',
  });
}

// SPY/SPX news feed: every stored headline for the selected day with its
// Claude sentiment classification, headline links to the Benzinga article.
// Kept 90 days (backend: news_headlines table).
export default function NewsFeed() {
  const [date, setDate] = useState(() => toDateStr(new Date()));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNews = useCallback(async (d, initial) => {
    try {
      const token = await getValidToken();
      const res = await fetch(`${API_URL}/api/news?date=${d}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setItems(await res.json());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      if (initial) setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchNews(date, true);
    const timer = setInterval(() => fetchNews(date, false), 15000);
    return () => clearInterval(timer);
  }, [date, fetchNews]);

  const counts = { bullish: 0, bearish: 0, neutral: 0 };
  for (const n of items) if (counts[n.sentiment] != null) counts[n.sentiment]++;

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <span className="text-xs font-bold px-3 py-1.5 rounded border"
          style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>
          SPY/SPX News
        </span>
        <span className="text-xs text-gray-500">Classified by AI for S&amp;P 500 impact · kept 90 days</span>
        <div className="flex-1" />
        {Object.entries(counts).map(([s, n]) => (
          <span key={s} className="text-xs font-semibold" style={{ color: SENTIMENT_COLOR[s] }}>
            {s.charAt(0).toUpperCase() + s.slice(1)} {n}
          </span>
        ))}
        <TradingDatePicker value={date} onChange={setDate} allowAllDays />
      </div>

      {loading ? (
        <div className="text-gray-500 text-sm text-center py-6">Loading…</div>
      ) : error ? (
        <div className="text-red-400 text-sm text-center py-6">Failed to load: {error}</div>
      ) : items.length === 0 ? (
        <div className="text-gray-500 text-sm text-center py-6">No news stored for this day</div>
      ) : (
        <div className="overflow-y-auto" style={{ maxHeight: '340px' }}>
          <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
            <thead className="sticky top-0" style={{ backgroundColor: '#111827' }}>
              <tr className="text-gray-500 border-b" style={{ borderColor: '#1f2937' }}>
                <th className="text-left py-2 pr-3 font-medium w-16">Time</th>
                <th className="text-left py-2 pr-3 font-medium">Headline</th>
                <th className="text-right py-2 font-medium w-28">Impact</th>
              </tr>
            </thead>
            <tbody>
              {items.map((n, i) => (
                <tr key={`${n.created_at}-${i}`} className="border-b" style={{ borderColor: '#1f2937' }}>
                  <td className="py-2 pr-3 text-gray-500 align-top whitespace-nowrap">{formatTime(n.created_at)}</td>
                  <td className="py-2 pr-3">
                    {n.url ? (
                      <a href={n.url} target="_blank" rel="noopener noreferrer"
                        className="text-gray-300 hover:text-blue-400 hover:underline">
                        {n.headline}
                      </a>
                    ) : (
                      <span className="text-gray-300">{n.headline}</span>
                    )}
                  </td>
                  <td className="py-2 text-right align-top">
                    {n.sentiment ? (
                      <span className="font-semibold px-1.5 py-0.5 rounded whitespace-nowrap"
                        style={{
                          backgroundColor: SENTIMENT_COLOR[n.sentiment] + '22',
                          color: SENTIMENT_COLOR[n.sentiment],
                        }}>
                        {n.sentiment === 'neutral'
                          ? 'Neutral'
                          : `${n.sentiment.charAt(0).toUpperCase()}${n.sentiment.slice(1)} ${n.confidence}%`}
                      </span>
                    ) : (
                      <span className="text-gray-600">pending…</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
